/** CRM structured addresses (aligned with ATW backend). */

import {
  addressRowHasContent,
  normalizeCanadianPostal,
  normalizeMexicanPostal,
  normalizeUkPostcode,
  validatePostalCode,
} from "@/lib/address/addressForm";
import { coerceIsoCountryCode, formatIsoCountryLabel, getIsoCountryByCode } from "@/lib/address/isoCountries";
import { findSubdivision } from "@/lib/address/subdivisions";
import type {
  CrmLeadFieldDefinition,
  CrmLeadFieldSectionId,
  CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";
import { crmLeadFieldIsHidden, seedStandardCrmLeadModuleFields } from "@/lib/crmLeadModuleFields";

export type CrmContactAddressType =
  | "billing"
  | "shipping"
  | "headquarters"
  | "home"
  | "other";

export type CrmContactAddress = {
  type: CrmContactAddressType;
  countryCode: string;
  adminArea?: string | null;
  locality?: string | null;
  postalCode?: string | null;
  line1?: string | null;
  line2?: string | null;
  recipientName?: string | null;
  isPrimary?: boolean;
};

export const CRM_CONTACT_ADDRESS_TYPES: CrmContactAddressType[] = [
  "billing",
  "shipping",
  "headquarters",
  "home",
];

export type CrmAddressTypeOptions = {
  addressTypes: CrmContactAddressType[];
  addressLabels: Partial<Record<CrmContactAddressType, string>>;
};

export function formatCrmAddressTypeLabel(type: CrmContactAddressType): string {
  if (type === "billing") return "Billing";
  if (type === "shipping") return "Shipping";
  if (type === "headquarters") return "Headquarters";
  if (type === "home") return "Home";
  return "Other";
}

export function defaultAddressCountryIdFromLeadFields(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  sectionId: Extract<CrmLeadFieldSectionId, "account_lead" | "contact_lead">,
): string {
  const fields: CrmLeadFieldDefinition[] = sections?.[sectionId]?.length
    ? sections[sectionId]
    : seedStandardCrmLeadModuleFields()[sectionId];
  const row = fields.find((f) => f.kind === "address_country_default");
  return coerceIsoCountryCode(row?.optionValue, "us");
}

function isAddressType(value: string): value is CrmContactAddressType {
  return (
    value === "billing" ||
    value === "shipping" ||
    value === "headquarters" ||
    value === "home" ||
    value === "other"
  );
}

export function addressOptionsFromLeadFields(
  sections: CrmLeadModuleFieldsDocument["sections"] | null | undefined,
  sectionId: Extract<CrmLeadFieldSectionId, "account_lead" | "contact_lead">,
): CrmAddressTypeOptions {
  const fields: CrmLeadFieldDefinition[] = sections?.[sectionId]?.length
    ? sections[sectionId]
    : seedStandardCrmLeadModuleFields()[sectionId];

  const addressTypes: CrmContactAddressType[] = [];
  const addressLabels: Partial<Record<CrmContactAddressType, string>> = {};

  for (const field of fields) {
    if (crmLeadFieldIsHidden(field)) continue;
    if (field.kind === "address_type" && field.optionValue && isAddressType(field.optionValue)) {
      const type = field.optionValue;
      if (!addressTypes.includes(type)) addressTypes.push(type);
      addressLabels[type] =
        field.optionLabel?.trim() || field.label?.trim() || formatCrmAddressTypeLabel(type);
    }
  }

  const standard = [...CRM_CONTACT_ADDRESS_TYPES];
  const merged = standard.filter((t) => addressTypes.length === 0 || addressTypes.includes(t));
  return {
    addressTypes: merged.length ? merged : standard,
    addressLabels,
  };
}

export function mergeAddressTypesForEditor(
  visibleTypes: CrmContactAddressType[],
  rows: CrmContactAddress[],
): CrmContactAddressType[] {
  const merged = [...visibleTypes];
  for (const row of rows) {
    if (!merged.includes(row.type)) merged.push(row.type);
  }
  return merged;
}

export function coerceAddressRowType(
  type: CrmContactAddressType,
  options: CrmAddressTypeOptions,
): CrmContactAddressType {
  if (options.addressTypes.includes(type)) return type;
  return options.addressTypes[0] || "billing";
}

export function displayCrmAddressTypeLabel(
  raw: string | undefined,
  type: CrmContactAddressType,
): string {
  return raw?.trim() || formatCrmAddressTypeLabel(type);
}

export function emptyAddressRow(
  countryCode = "us",
  isPrimary = false,
  type: CrmContactAddressType = "billing",
): CrmContactAddress {
  return {
    type,
    countryCode: coerceIsoCountryCode(countryCode),
    adminArea: "",
    locality: "",
    postalCode: "",
    line1: "",
    line2: "",
    recipientName: "",
    isPrimary,
  };
}

function normalizeAddressType(value: unknown): CrmContactAddressType {
  const v = String(value || "").trim().toLowerCase();
  if (isAddressType(v)) return v;
  return "billing";
}

function normalizeAddressRow(raw: unknown): CrmContactAddress | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const countryCode = coerceIsoCountryCode(row.countryCode ?? row.country);
  const adminAreaRaw = String(row.adminArea ?? row.state ?? "").trim();
  const adminArea = adminAreaRaw ? adminAreaRaw.toUpperCase() : "";
  const locality = String(row.locality ?? row.city ?? "").trim() || null;
  let postalCode = String(row.postalCode ?? row.zip ?? "").trim() || null;
  if (postalCode && countryCode === "ca") {
    postalCode = normalizeCanadianPostal(postalCode);
  }
  if (postalCode && countryCode === "mx") {
    postalCode = normalizeMexicanPostal(postalCode);
  }
  if (postalCode && (countryCode === "gb" || countryCode === "uk")) {
    postalCode = normalizeUkPostcode(postalCode);
  }
  const line1 = String(row.line1 ?? row.street1 ?? "").trim() || null;
  const line2 = String(row.line2 ?? row.street2 ?? "").trim() || null;
  const recipientName = String(row.recipientName ?? row.attention ?? "").trim() || null;

  if (
    !addressRowHasContent({
      line1,
      line2,
      locality,
      adminArea,
      postalCode,
      recipientName,
    })
  ) {
    return null;
  }

  const sub = findSubdivision(countryCode, adminArea);
  return {
    type: normalizeAddressType(row.type),
    countryCode,
    adminArea: sub?.code || adminArea || null,
    locality,
    postalCode,
    line1,
    line2,
    recipientName,
    isPrimary: row.isPrimary === true,
  };
}

export function ensureSinglePrimaryAddress(addresses: CrmContactAddress[]): CrmContactAddress[] {
  if (!addresses.length) return addresses;
  const primaries = addresses.filter((a) => a.isPrimary);
  if (primaries.length === 1) return addresses;
  const preferred =
    primaries[0] ||
    addresses.find((a) => a.isPrimary) ||
    addresses.find((a) => a.type === "headquarters") ||
    addresses.find((a) => a.type === "billing") ||
    addresses[0];
  const key = (a: CrmContactAddress) =>
    [a.line1, a.locality, a.postalCode, a.countryCode, a.type].join("|");
  const preferredKey = key(preferred);
  return addresses.map((a) => ({
    ...a,
    isPrimary: key(a) === preferredKey,
  }));
}

export function normalizeContactAddressesInput(raw: unknown): CrmContactAddress[] {
  if (!Array.isArray(raw)) return [];
  const out: CrmContactAddress[] = [];
  for (const item of raw) {
    const row = normalizeAddressRow(item);
    if (row) out.push(row);
  }
  return ensureSinglePrimaryAddress(out);
}

export function formatCrmAddressOneLine(address: CrmContactAddress | null | undefined): string {
  if (!address) return "";
  const country = getIsoCountryByCode(address.countryCode);
  const sub = findSubdivision(address.countryCode, address.adminArea);
  const cc = address.countryCode.trim().toLowerCase();
  if (cc === "gb" || cc === "uk") {
    const lines = [
      address.line1?.trim() || "",
      address.line2?.trim() || "",
      address.locality?.trim() || "",
      address.adminArea?.trim() || "",
      address.postalCode?.trim() || "",
      country ? formatIsoCountryLabel(country) : "United Kingdom",
    ].filter(Boolean);
    return lines.join(", ");
  }
  const lines = [
    address.recipientName?.trim() || "",
    address.line1?.trim() || "",
    address.line2?.trim() || "",
    [address.locality?.trim(), sub?.name || address.adminArea?.trim(), address.postalCode?.trim()]
      .filter(Boolean)
      .join(", "),
    country ? formatIsoCountryLabel(country) : address.countryCode.toUpperCase(),
  ].filter(Boolean);
  return lines.join(", ");
}

export function pickPrimaryAddress(addresses: CrmContactAddress[]): CrmContactAddress | null {
  const filled = addresses.filter((a) =>
    addressRowHasContent({
      line1: a.line1,
      line2: a.line2,
      locality: a.locality,
      adminArea: a.adminArea,
      postalCode: a.postalCode,
      recipientName: a.recipientName,
    }),
  );
  if (!filled.length) return null;
  const primary = filled.find((a) => a.isPrimary);
  const hq = filled.find((a) => a.type === "headquarters");
  const billing = filled.find((a) => a.type === "billing");
  return primary || hq || billing || filled[0];
}

export function legacyCityStateFromAddresses(addresses: CrmContactAddress[]): {
  city: string | null;
  state: string | null;
} {
  const pick = pickPrimaryAddress(addresses);
  if (!pick) return { city: null, state: null };
  const sub = findSubdivision(pick.countryCode, pick.adminArea);
  return {
    city: pick.locality?.trim() || null,
    state: sub?.code || pick.adminArea?.trim() || null,
  };
}

export function contactAddressesToApiBody(addresses: CrmContactAddress[]): CrmContactAddress[] {
  return ensureSinglePrimaryAddress(
    addresses
      .filter((a) =>
        addressRowHasContent({
          line1: a.line1,
          line2: a.line2,
          locality: a.locality,
          adminArea: a.adminArea,
          postalCode: a.postalCode,
          recipientName: a.recipientName,
        }),
      )
      .map((a) => ({
        ...a,
        countryCode: coerceIsoCountryCode(a.countryCode),
        adminArea: a.adminArea?.trim() || null,
        locality: a.locality?.trim() || null,
        postalCode:
          a.countryCode === "ca" && a.postalCode
            ? normalizeCanadianPostal(a.postalCode)
            : a.countryCode === "mx" && a.postalCode
              ? normalizeMexicanPostal(a.postalCode)
              : (a.countryCode === "gb" || a.countryCode === "uk") && a.postalCode
                ? normalizeUkPostcode(a.postalCode)
                : a.postalCode?.trim() || null,
        line1: a.line1?.trim() || null,
        line2: a.line2?.trim() || null,
        recipientName: a.recipientName?.trim() || null,
      })),
  );
}

export function firstInvalidCrmAddress(
  addresses: CrmContactAddress[],
): { address: CrmContactAddress; message: string } | null {
  for (const row of addresses) {
    if (
      !addressRowHasContent({
        line1: row.line1,
        line2: row.line2,
        locality: row.locality,
        adminArea: row.adminArea,
        postalCode: row.postalCode,
        recipientName: row.recipientName,
      })
    ) {
      continue;
    }
    const postalError = validatePostalCode(row.countryCode, row.postalCode || "");
    if (postalError) return { address: row, message: postalError };
  }
  return null;
}

export function assertValidCrmAddresses(addresses: CrmContactAddress[], contextLabel: string): void {
  const invalid = firstInvalidCrmAddress(addresses);
  if (invalid) {
    throw new Error(`${contextLabel}: ${invalid.message}`);
  }
}

export function setAddressPrimaryAtIndex(
  addresses: CrmContactAddress[],
  index: number,
): CrmContactAddress[] {
  return addresses.map((row, i) => ({ ...row, isPrimary: i === index }));
}

export function removeAddressAtIndex(
  addresses: CrmContactAddress[],
  index: number,
): CrmContactAddress[] {
  const next = addresses.filter((_, i) => i !== index);
  if (!next.length) return next;
  if (next.some((a) => a.isPrimary)) return ensureSinglePrimaryAddress(next);
  return ensureSinglePrimaryAddress(next.map((row, i) => ({ ...row, isPrimary: i === 0 })));
}

export function appendAddressRow(
  addresses: CrmContactAddress[],
  row: CrmContactAddress,
): CrmContactAddress[] {
  if (!addresses.length) {
    return ensureSinglePrimaryAddress([{ ...row, isPrimary: true }]);
  }
  return ensureSinglePrimaryAddress([...addresses, { ...row, isPrimary: false }]);
}
